import { Component, Input, Output, EventEmitter, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { WindowService } from 'src/shared/providers/window.service';
import { AboutThisPageService } from 'src/providers/about-this-page/about-this-page.service';
import { TranslationPipe } from 'src/shared/pipes/translation.pipe';

@Component({
  selector: 'tidy-about-this-page',
  templateUrl: './about-this-page.component.html',
  styleUrls: ['./about-this-page.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class AboutThisPageComponent implements OnInit {

  @Input() page: string;
  @Input() overlayTop: string;
  @Output() dismissed = new EventEmitter<void>();
  pageData: { name: string, title: string, description: string, buttonText: string };
  showAboutThisPage = false;
  private translationPipe = new TranslationPipe();

  constructor(
    public windowService: WindowService,
    private aboutThisPageService: AboutThisPageService
  ) {}

  ngOnInit() {
    this.pageData = this.getAboutThisPageData(this.page);
    this.showAboutThisPage = this.aboutThisPageService.checkIfShouldShowAboutThisPage(this.page);
    if (!this.pageData) {
      this.dismissAboutThisPage();
    }
  }

  dismissAboutThisPage() {
    this.showAboutThisPage = false;
    this.aboutThisPageService.dismissAboutThisPage(this.pageData?.name ?? this.page);
    this.dismissed.emit();
  }

  getAboutThisPageData(page: string) {
    const storedAccountType = localStorage.getItem('accountType') || 'rental';
    const accountTypeMap = {
      'rental': 'rental',
      'residential': 'residential',
      'regular': 'residential',
      'office': 'office',
      'company': 'office'
    };
    const accountType = accountTypeMap[storedAccountType] || 'rental';

    const descriptions = {
      rental: {
        'strategy': {
          name: 'strategy',
          description: 'See revenue, expenses, and net income across every property at a glance. Spot trends and act on suggestions to grow profit.',
          buttonText: 'Ok'
        },
        'monthly-pl': {
          name: 'monthly-pl',
          description: 'Every line of income and expense, by month and per property. Edit any cell to override the computed value or just track what actually happened.',
          buttonText: 'Ok'
        },
        'schedule': {
          name: 'schedule',
          description: 'Track all cleanings, maintenance, and reservations in realtime.',
          buttonText: 'Ok'
        },
        'messages': {
          name: 'messages',
          description: 'View messages from pros and automate responses following your rules.',
          buttonText: 'Ok'
        },
        'bills': {
          name: 'bills',
          description: 'Set up rules to pay pros on autopilot.',
          buttonText: 'Ok'
        },
        'restocking': {
          name: 'restocking',
          description: 'Automate restocking of things like soaps, coffee, toilet paper, & more.',
          buttonText: 'Ok'
        },
        'maintenance': {
          name: 'maintenance',
          description: 'Smart maintenance suggestions custom to you. What to do, what to skip, & how to save.',
          buttonText: 'Ok'
        },
        'assets': {
          name: 'assets',
          description: 'Add appliances or other assets to track maintenance and get suggestions.',
          buttonText: 'Ok'
        },
        'checklists': {
          name: 'checklists',
          description: 'Jobs get done right with digital checklists. Easily share. No download or signup required.',
          buttonText: 'Ok'
        },
        'upsells': {
          name: 'upsells',
          description: 'Enable automated handling of early check-in, late check-out, and mid-stay clean requests.',
          buttonText: 'Ok'
        },
        'pros': {
          name: 'pros',
          description: 'Use TIDY with any pro. If they don\'t use TIDY, we can automate via SMS or phone.',
          buttonText: 'Ok'
        },
        'guests': {
          name: 'guests',
          description: 'Track guest contact info from every reservation. Reach past guests for repeat bookings and reviews.',
          buttonText: 'Ok'
        },
        'noise-monitoring': {
          name: 'noise-monitoring',
          description: 'Monitor noise levels across your properties in realtime.',
          buttonText: 'Ok'
        },
        'transactions': {
          name: 'transactions',
          description: 'Track and categorize property expenses automatically from your connected bank accounts.',
          buttonText: 'Ok'
        },
        'taxes': {
          name: 'taxes',
          description: 'Set up tax rates per property to automate collection and remittance tracking.',
          buttonText: 'Ok'
        },
        'properties': {
          name: 'properties',
          description: 'Add and manage your rental properties in one place.',
          buttonText: 'Ok'
        },
        'access': {
          name: 'access',
          description: 'View and manage smart lock access across your properties.',
          buttonText: 'Ok'
        },
        'listings': {
          name: 'listings',
          description: 'Connect and manage your property listings across booking platforms.',
          buttonText: 'Ok'
        },
        'reviews': {
          name: 'reviews',
          description: 'Monitor and respond to guest reviews across all channels.',
          buttonText: 'Ok'
        },
        'guidebook': {
          name: 'guidebook',
          description: 'Create digital guidebooks for your guests with check-in instructions, house rules, and local recommendations.',
          buttonText: 'Ok'
        },
        'wifi-marketing': {
          name: 'wifi-marketing',
          description: 'Capture guest contact information and promote upsells through your property Wi-Fi network.',
          buttonText: 'Ok'
        },
        'compliance': {
          name: 'compliance',
          description: 'Track permits, licenses, and regulatory requirements for each property.',
          buttonText: 'Ok'
        },
        'insurance': {
          name: 'insurance',
          description: 'Manage insurance policies, coverage limits, and renewal dates across your properties.',
          buttonText: 'Ok'
        },
        'direct-booking': {
          name: 'direct-booking',
          description: 'Set up branded direct booking websites for each property — customize your site, add a custom domain, and take reservations without channel fees.',
          buttonText: 'Ok'
        }
      },
      residential: {
        'strategy': {
          name: 'strategy',
          description: 'See income, expenses, and net cost for your home at a glance. Spot trends and find ways to save.',
          buttonText: 'Ok'
        },
        'monthly-pl': {
          name: 'monthly-pl',
          description: 'Every line of income and expense for your home, by month. Edit any cell to override the computed value or just track what actually happened.',
          buttonText: 'Ok'
        },
        'schedule': {
          name: 'schedule',
          description: 'Track all cleanings and maintenance for your home in realtime.',
          buttonText: 'Ok'
        },
        'messages': {
          name: 'messages',
          description: 'View messages from your service pros.',
          buttonText: 'Ok'
        },
        'maintenance': {
          name: 'maintenance',
          description: 'Smart maintenance suggestions custom to you. What to do, what to skip, & how to save.',
          buttonText: 'Ok'
        },
        'assets': {
          name: 'assets',
          description: 'Add home appliances and other assets to track maintenance and get suggestions.',
          buttonText: 'Ok'
        },
        'checklists': {
          name: 'checklists',
          description: 'Jobs get done right with digital checklists. Easily share with your service pros.',
          buttonText: 'Ok'
        },
        'pros': {
          name: 'pros',
          description: 'TIDY can send pros for any home service or you can add your own.',
          buttonText: 'Ok'
        },
        'guests': {
          name: 'guests',
          description: 'Keep tenant and household contact info in one place for easy access and communication.',
          buttonText: 'Ok'
        },
        'noise-monitoring': {
          name: 'noise-monitoring',
          description: 'Monitor noise levels across your properties in realtime.',
          buttonText: 'Ok'
        },
        'transactions': {
          name: 'transactions',
          description: 'Track and categorize property expenses automatically from your connected bank accounts.',
          buttonText: 'Ok'
        },
        'taxes': {
          name: 'taxes',
          description: 'Set up tax rates per property to automate collection and remittance tracking.',
          buttonText: 'Ok'
        },
        'properties': {
          name: 'properties',
          description: 'Add and manage your properties in one place.',
          buttonText: 'Ok'
        },
        'access': {
          name: 'access',
          description: 'View and manage smart lock access across your properties.',
          buttonText: 'Ok'
        },
        'listings': {
          name: 'listings',
          description: 'Connect and manage your property listings across booking platforms.',
          buttonText: 'Ok'
        },
        'reviews': {
          name: 'reviews',
          description: 'Monitor and respond to guest reviews across all channels.',
          buttonText: 'Ok'
        },
        'guidebook': {
          name: 'guidebook',
          description: 'Create digital guidebooks for your guests with check-in instructions, house rules, and local recommendations.',
          buttonText: 'Ok'
        },
        'wifi-marketing': {
          name: 'wifi-marketing',
          description: 'Capture guest contact information and promote upsells through your property Wi-Fi network.',
          buttonText: 'Ok'
        },
        'compliance': {
          name: 'compliance',
          description: 'Track permits, licenses, and regulatory requirements for each property.',
          buttonText: 'Ok'
        },
        'insurance': {
          name: 'insurance',
          description: 'Manage insurance policies, coverage limits, and renewal dates across your properties.',
          buttonText: 'Ok'
        },
        'direct-booking': {
          name: 'direct-booking',
          description: 'Set up branded direct booking websites for each property — customize your site, add a custom domain, and take reservations without channel fees.',
          buttonText: 'Ok'
        }
      },
      office: {
        'strategy': {
          name: 'strategy',
          description: 'See income, expenses, and net cost across your offices at a glance. Spot trends and find ways to save.',
          buttonText: 'Ok'
        },
        'monthly-pl': {
          name: 'monthly-pl',
          description: 'Every line of income and expense by month per office. Edit any cell to override the computed value or just track what actually happened.',
          buttonText: 'Ok'
        },
        'schedule': {
          name: 'schedule',
          description: 'Track all cleanings and maintenance for your office in realtime.',
          buttonText: 'Ok'
        },
        'messages': {
          name: 'messages',
          description: 'View messages from your service pros.',
          buttonText: 'Ok'
        },
        'restocking': {
          name: 'restocking',
          description: 'Automate restocking of office supplies like soaps, coffee, paper products, & more.',
          buttonText: 'Ok'
        },
        'maintenance': {
          name: 'maintenance',
          description: 'Smart maintenance suggestions custom to you. What to do, what to skip, & how to save.',
          buttonText: 'Ok'
        },
        'assets': {
          name: 'assets',
          description: 'Add office equipment or other assets to track maintenance and get suggestions.',
          buttonText: 'Ok'
        },
        'checklists': {
          name: 'checklists',
          description: 'Jobs get done right with digital checklists. Easily share with your service pros.',
          buttonText: 'Ok'
        },
        'pros': {
          name: 'pros',
          description: 'TIDY can send pros for any office service or you can add your own.',
          buttonText: 'Ok'
        },
        'guests': {
          name: 'guests',
          description: 'Keep visitor and tenant contact info in one place for easy access and communication.',
          buttonText: 'Ok'
        },
        'noise-monitoring': {
          name: 'noise-monitoring',
          description: 'Monitor noise levels across your properties in realtime.',
          buttonText: 'Ok'
        },
        'transactions': {
          name: 'transactions',
          description: 'Track and categorize property expenses automatically from your connected bank accounts.',
          buttonText: 'Ok'
        },
        'taxes': {
          name: 'taxes',
          description: 'Set up tax rates per property to automate collection and remittance tracking.',
          buttonText: 'Ok'
        },
        'properties': {
          name: 'properties',
          description: 'Add and manage your office locations in one place.',
          buttonText: 'Ok'
        },
        'access': {
          name: 'access',
          description: 'View and manage smart lock access across your properties.',
          buttonText: 'Ok'
        },
        'listings': {
          name: 'listings',
          description: 'Connect and manage your property listings across booking platforms.',
          buttonText: 'Ok'
        },
        'reviews': {
          name: 'reviews',
          description: 'Monitor and respond to guest reviews across all channels.',
          buttonText: 'Ok'
        },
        'guidebook': {
          name: 'guidebook',
          description: 'Create digital guidebooks with check-in instructions, office rules, and local recommendations.',
          buttonText: 'Ok'
        },
        'wifi-marketing': {
          name: 'wifi-marketing',
          description: 'Capture contact information and promote services through your property Wi-Fi network.',
          buttonText: 'Ok'
        },
        'compliance': {
          name: 'compliance',
          description: 'Track permits, licenses, and regulatory requirements for each property.',
          buttonText: 'Ok'
        },
        'insurance': {
          name: 'insurance',
          description: 'Manage insurance policies, coverage limits, and renewal dates across your properties.',
          buttonText: 'Ok'
        },
        'direct-booking': {
          name: 'direct-booking',
          description: 'Set up branded direct booking websites for each property — customize your site, add a custom domain, and take reservations without channel fees.',
          buttonText: 'Ok'
        }
      }
    };

    const data = descriptions[accountType] || descriptions['rental'];
    const pageData = data[page];
    if (pageData) {
      return {
        ...pageData,
        description: this.translationPipe.transform(pageData.description),
        buttonText: this.translationPipe.transform(pageData.buttonText)
      };
    }
    return pageData;
  }

}
